import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import './UserList.css';
import './WeeklyCommission.css';
import { ADMIN_BASE_URL } from './config';
import { toast } from 'react-toastify';

function WeeklyCommission() {
    const [hierarchy, setHierarchy] = useState([]);
    const [expandedRowKey, setExpandedRowKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const inputRefs = useRef({});

    useEffect(() => {
        const ac = new AbortController();
        fetchWeeklyCommissions(ac.signal);
        return () => ac.abort();
    }, []);

    const FETCH_TIMEOUT_MS = 15000;

    const fetchWeeklyCommissions = async (signal, options = {}) => {
        const { preserveExpanded = false } = options;
        const token = localStorage.getItem('token');
        setError(null);
        setLoading(true);

        const abortController = new AbortController();
        const timeoutIdRef = setTimeout(() => abortController.abort(), FETCH_TIMEOUT_MS);
        if (signal) {
            signal.addEventListener('abort', () => abortController.abort());
        }

        try {
            const response = await fetch(
                `${ADMIN_BASE_URL}/usercommission/admin/commission-hierarchy`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    signal: abortController.signal,
                }
            );

            clearTimeout(timeoutIdRef);

            const contentType = response.headers.get('content-type');
            const result = contentType?.includes('application/json')
                ? await response.json()
                : { success: false, message: 'Server returned non-JSON response' };

            if (!response.ok || !result.success) {
                throw new Error(result?.message || 'Failed to load commission data');
            }

            const tree = result.hierarchy || [];
            setHierarchy(tree);
            setExpandedRowKey(null);
        } catch (err) {
            console.error('Error loading weekly commissions:', err);
            if (err.name === 'AbortError') {
                setError('Request timed out or was cancelled. Check your connection and try again.');
            } else {
                setError(err?.message || 'Failed to load weekly commissions');
            }
            setHierarchy([]);
        } finally {
            clearTimeout(timeoutIdRef);
            setLoading(false);
        }
    };

    const updateCommissionInHierarchy = (tree, commissionId, updates) => {
        if (!tree || !Array.isArray(tree)) return tree;
        return tree.map((item) => {
            if (item.commission_id === commissionId) {
                return { ...item, ...updates };
            }
            if (item.children && item.children.length > 0) {
                return { ...item, children: updateCommissionInHierarchy(item.children, commissionId, updates) };
            }
            return item;
        });
    };

    const handleSubmitCommission = async (node) => {
        if (!node.commission_id) {
            toast.error('Invalid commission entry');
            return;
        }

        const refs = inputRefs.current[node.commission_id] || {};
        const selectedStatus = (refs.statusSelect && refs.statusSelect.value) || node.paymentStatus || 'pending';
        const transaction_reference = (refs.txnRef && refs.txnRef.value) || node.transactionRef || '';
        const vap_account = (refs.vapAcc && refs.vapAcc.value) || node.vapAccount || '';
        let payment_date = (refs.paymentDate && refs.paymentDate.value) || '';
        if (!payment_date && node.paymentDate) {
            const d = new Date(node.paymentDate);
            payment_date = d.toISOString().split('T')[0];
        }
        if (!payment_date) payment_date = new Date().toISOString().split('T')[0];

        const levelLabel = node.levelOrdinal != null ? `L${node.levelOrdinal}` : `Level ${node.level}`;
        const confirmMessage = selectedStatus === 'paid'
            ? `Mark commission (${levelLabel}, $${node.amount || 0}) as paid?`
            : `Mark commission (${levelLabel}, $${node.amount || 0}) as pending?`;
        if (!window.confirm(confirmMessage)) return;

        try {
            const token = localStorage.getItem('token');
            const body = selectedStatus === 'paid'
                ? { payment_status: 'paid', payment_date, transaction_reference, vap_account, paid_amount: node.amount }
                : { payment_status: 'pending' };

            const response = await fetch(
                `${ADMIN_BASE_URL}/usercommission/update/commission-payment-info/${node.commission_id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(body),
                }
            );

            const result = await response.json();
            if (!response.ok) {
                toast.error(result?.message || 'Update failed');
                return;
            }
            const updates = {
                paymentStatus: selectedStatus,
                transactionRef: transaction_reference,
                vapAccount: vap_account,
                paymentDate: selectedStatus === 'paid' ? payment_date : null,
            };
            setHierarchy((prev) => updateCommissionInHierarchy(prev, node.commission_id, updates));
            toast.success(selectedStatus === 'paid' ? 'Commission marked as paid' : 'Commission marked as pending');
        } catch (err) {
            console.error('Error updating commission:', err);
            toast.error('Network error');
        }
    };

    // Build summary rows: one per (week, earner) for the drill-down table
    const summaryRows = [];
    hierarchy.forEach((week, weekIndex) => {
        (week.children || []).forEach((earner, earnerIndex) => {
            const firstReferred = earner.children?.[0]?.name || '—';
            summaryRows.push({
                key: `${weekIndex}-${earnerIndex}`,
                weekIndex,
                earnerIndex,
                weekName: week.name,
                userName: earner.name,
                referredTo: firstReferred,
                amount: earner.branchTotal ?? 0,
                status: earner.branchStatus || earner.paymentStatus || 'pending',
                earner,
            });
        });
    });

    const toggleRow = (key) => {
        setExpandedRowKey((prev) => (prev === key ? null : key));
    };

    const formatStatusLabel = (s) => {
        if (s === 'partially_paid') return 'Partially Paid';
        if (s === 'paid') return 'Completed';
        if (s === 'pending') return 'Not Started';
        return (s || 'pending').charAt(0).toUpperCase() + (s || 'pending').slice(1);
    };

    const formatDate = (d) => {
        if (!d) return '';
        const x = new Date(d);
        const pad = (n) => (n < 10 ? '0' + n : n);
        return `${pad(x.getDate())}/${pad(x.getMonth() + 1)}/${String(x.getFullYear()).slice(-2)}`;
    };

    const VAP_OPTIONS = ['Wallet A', 'Wallet B', 'VAP Account'];

    function TreeNode({ node, depth = 0 }) {
        if (!node || (node.isWeek || node.isEarner)) return null;

        const hasChildren = node.children && node.children.length > 0;
        const children = node.children || [];

        return (
            <div className={`tree-node-wrapper ${depth > 0 ? 'tree-node-wrapper--nested' : ''}`} data-depth={depth}>
                <div className="tree-node tree-node--item">
                    <CommissionCard node={node} depth={depth} />
                </div>
                {hasChildren && (
                    <div className="tree-children">
                        {children.map((child) => (
                            <TreeNode
                                key={`${child.userId}-${child.commission_id || ''}-${child.paymentStatus || 'pending'}`}
                                node={child}
                                depth={depth + 1}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Dynamic level: use levelOrdinal from API (L1, L2, L3, …), else tree depth, else 1
    const displayLevel = (node) => {
        if (node.levelOrdinal != null) return node.levelOrdinal;
        if (node.treeLevel != null) return node.treeLevel;
        return 1;
    };

    function CommissionCard({ node, depth = 0 }) {
        if (!node || (node.isWeek || node.isEarner)) return null;
        const amount = node.amount || 0;
        const levelNum = displayLevel(node);

        const isPaid = node.paymentStatus === 'paid';
        const cid = node.commission_id;
        if (cid) {
            if (!inputRefs.current[cid]) inputRefs.current[cid] = {};
        }

        return (
            <div className={`commission-card ${isPaid ? 'paid' : 'pending'}`}>
                    <div className="commission-card-top">
                        <div className="commission-card-user">
                            <span className={`level-dot l${levelNum}`} data-level={levelNum} />
                            <span className="name">{node.name}</span>
                            {(node.referralUserName != null && node.referralUserName !== '') && (
                                <div className="referred-by">Referred by: {node.referralUserName}</div>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span className="level-pill">L{levelNum}</span>
                            <span className={`commission-amount ${isPaid ? 'paid' : 'pending'}`}>
                                ${Number(amount).toFixed(2)}
                            </span>
                        </div>
                    </div>
                    <div className="commission-card-bottom">
                        <select
                            ref={(el) => { if (cid) inputRefs.current[cid].statusSelect = el; }}
                            defaultValue={node.paymentStatus || 'pending'}
                            style={{ minWidth: 100 }}
                            className="commission-card-status-select"
                        >
                            <option value="paid">Paid</option>
                            <option value="pending">Pending</option>
                        </select>
                        <input
                            ref={(el) => { if (cid) inputRefs.current[cid].txnRef = el; }}
                            type="text"
                            placeholder="TXN Ref"
                            defaultValue={node.transactionRef || ''}
                            style={{ minWidth: 90 }}
                            className="commission-card-input"
                        />
                        <input
                            ref={(el) => { if (cid) inputRefs.current[cid].paymentDate = el; }}
                            type="date"
                            placeholder="dd/mm/yy"
                            defaultValue={
                                node.paymentDate
                                    ? new Date(node.paymentDate).toISOString().split('T')[0]
                                    : ''
                            }
                            style={{ minWidth: 120 }}
                            className="commission-card-input"
                        />
                        <select
                            ref={(el) => { if (cid) inputRefs.current[cid].vapAcc = el; }}
                            defaultValue={node.vapAccount || ''}
                            style={{ minWidth: 100 }}
                            className="commission-card-input"
                        >
                            <option value="">VAP Acc</option>
                            {VAP_OPTIONS.map((o) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className={isPaid ? 'btn-save-paid' : 'btn-mark-paid pending'}
                            onClick={() => handleSubmitCommission(node)}
                            title={isPaid ? 'Save changes' : 'Submit'}
                        >
                            <CheckCircleIcon size={20} />
                        </button>
                    </div>
                </div>
        );
    }

    return (
        <Layout>
            <div className="userlist-container weekly-commission">
                <header className="weekly-commission-header">
                    <h1 className="weekly-commission-title">Weekly Commission Review</h1>
                    <p className="weekly-commission-subtitle">Expand a row to view the referral tree and manage payments</p>
                </header>

                {error && (
                    <div className="weekly-commission-error">
                        <span className="weekly-commission-error-message">{error}</span>
                        <button
                            type="button"
                            className="weekly-commission-retry-btn"
                            onClick={() => fetchWeeklyCommissions()}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {loading ? (
                    <div className="weekly-commission-loading">
                        <div className="weekly-commission-spinner" aria-hidden />
                        <p>Loading weekly commissions...</p>
                    </div>
                ) : hierarchy.length === 0 ? (
                    <div className="weekly-commission-empty">
                        <p className="weekly-commission-empty-title">No commission data</p>
                        <p className="weekly-commission-empty-text">Commission entries will appear here once available.</p>
                    </div>
                ) : (
                    <div className="commission-drilldown-table-wrapper">
                        <table className="commission-drilldown-table">
                            <thead>
                                <tr>
                                    <th className="col-user">Week</th>
                                    <th className="col-user">User Name</th>
                                    <th className="col-referred">Referred To</th>
                                    <th className="col-amount">Commission Amount</th>
                                    <th className="col-status">Payment Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summaryRows.map((row, index) => (
                                    <React.Fragment key={row.key}>
                                        <tr
                                            className={`commission-summary-row ${index % 2 === 1 ? 'row-alt' : ''}`}
                                            onClick={() => toggleRow(row.key)}
                                        >
                                            <td className="col-week">
                                                <span className="drilldown-chevron" aria-hidden>
                                                    <ChevronIcon expanded={expandedRowKey === row.key} />
                                                </span>
                                                {row.weekName}
                                            </td>
                                            <td className="col-user">{row.userName}</td>
                                            <td className="col-referred">{row.referredTo}</td>
                                            <td className="col-amount amount-cell">
                                                ${Number(row.amount).toFixed(2)}
                                            </td>
                                            <td className="col-status">
                                                <span className={`status-pill status-${row.status === 'paid' ? 'completed' : row.status === 'partially_paid' ? 'partially_paid' : 'not_started'}`}>
                                                    {row.status === 'paid' && <CheckIcon size={14} />}
                                                    {formatStatusLabel(row.status)}
                                                </span>
                                            </td>
                                        </tr>
                                        {expandedRowKey === row.key && (
                                            <tr className="commission-detail-row">
                                                <td colSpan={5}>
                                                    <div className="referral-tree-section">
                                                        <div className="referral-tree-title">REFERRAL TREE</div>
                                                        <div className="tree-view">
                                                            <div className="tree-node tree-node--parent">
                                                                <span className="tree-node-icon" aria-hidden>
                                                                    <TreeIcon />
                                                                </span>
                                                                <span className="tree-node-label">{row.earner.name}</span>
                                                            </div>
                                                            <div className="tree-children">
                                                                {(row.earner.children || []).map((node) => (
                                                                    <TreeNode
                                                                        key={`${node.userId}-${node.commission_id || ''}-${node.paymentStatus || 'pending'}`}
                                                                        node={node}
                                                                        depth={0}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Layout>
    );
}

function CheckIcon({ size = 18 }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
        </svg>
    );
}

function CheckCircleIcon({ size = 20 }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size} aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <path d="M9 12l2 2 4-4" />
        </svg>
    );
}

function ChevronIcon({ expanded }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            width={16}
            height={16}
            className={`drilldown-chevron-icon ${expanded ? 'expanded' : ''}`}
            aria-hidden
        >
            {/* Single right-pointing chevron; rotated 90deg when expanded to point down */}
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" />
        </svg>
    );
}

function TreeIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" width={20} height={20}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
        </svg>
    );
}

export default WeeklyCommission;
